import { CheckCheck } from "lucide-react";

import { EventsEmptyState } from "@/components/events/EventsEmptyState";
import { EventsErrorState } from "@/components/events/EventsErrorState";
import { EventsFilterBar } from "@/components/events/EventsFilterBar";
import { EventsPagination } from "@/components/events/EventsPagination";
import { EventsTable } from "@/components/events/EventsTable";
import { EventsTableSkeleton } from "@/components/events/EventsTableSkeleton";
import { Button } from "@/components/ui/button";
import { useEventsFilters } from "@/hooks/useEventsFilters";
import { useEventsQuery } from "@/hooks/useEventsQuery";
import { useMarkAllEventsSeen } from "@/hooks/useMarkEventSeen";
import { isDefaultQuery } from "@/lib/eventsQueryParams";

export function EventsPage() {
  const { query, setPatch, clearAll } = useEventsFilters();
  const eventsQuery = useEventsQuery(query);
  const markAllSeen = useMarkAllEventsSeen();

  const response = eventsQuery.data;
  const hasFilters = !isDefaultQuery(query);
  const isInitialLoading = eventsQuery.isLoading && response === undefined;

  return (
    <div className="container space-y-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Eventi</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Eventi estratti dalle email in ingresso.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => markAllSeen.mutate()}
          disabled={markAllSeen.isPending}
        >
          <CheckCheck />
          Segna tutti come letti
        </Button>
      </div>

      <EventsFilterBar query={query} onPatch={setPatch} onClear={clearAll} />

      {isInitialLoading ? (
        <EventsTableSkeleton rows={Math.min(query.pageSize, 10)} />
      ) : eventsQuery.isError ? (
        <EventsErrorState
          message={
            eventsQuery.error instanceof Error
              ? eventsQuery.error.message
              : undefined
          }
          onRetry={() => eventsQuery.refetch()}
        />
      ) : response && response.items.length === 0 ? (
        <EventsEmptyState hasFilters={hasFilters} onClear={clearAll} />
      ) : response ? (
        <>
          <EventsTable
            items={response.items}
            query={query}
            onSort={setPatch}
            isFetching={eventsQuery.isFetching}
          />
          <EventsPagination
            page={response.page}
            pageSize={response.pageSize}
            total={response.total}
            totalPages={response.totalPages}
            onPageChange={(page) => setPatch({ page })}
            onPageSizeChange={(pageSize) => setPatch({ pageSize, page: 1 })}
          />
        </>
      ) : null}
    </div>
  );
}
